import { useEffect } from 'react';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useRequiresAuthorization } from '@ibk/shared-permissioning';
import { FeatureFlagUseCase, PermissionsDomains, PermissionsResources, ProductAlias, } from '@ibk/shared-types';
import { normalizeProductResponse, productApi } from '../services/home/productApi';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectContaDigitalProduct } from '../store/selectors/productsSelectors';
import { selectSelectedAccountId } from '../store/selectors/userSelectors';
import { setProductData, setProductError, setProductLoading } from '../store/slices/productsSlice';
export const useContaDigital = ({ enabled = true } = {}) => {
    const dispatch = useAppDispatch();
    const selectedAccountId = useAppSelector(selectSelectedAccountId);
    const contaDigitalProduct = useAppSelector(selectContaDigitalProduct);
    const canSeeBalance = useRequiresAuthorization(PermissionsDomains.ACCOUNT, [
        PermissionsResources.ACCOUNT_VIEW_BALANCE,
    ]);
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
    const queryParams = {
        productAlias: ProductAlias.CONTA_DIGITAL,
        selectedCompanyPartyId: selectedAccountId || '',
        accountPjCanSeeBalance: canSeeBalance,
    };
    const skipQuery = !enabled || !selectedAccountId;
    const queryOptions = { refetchOnMountOrArgChange: true, refetchOnFocus: false, refetchOnReconnect: true };
    const queryV1 = productApi.useGetHomeProductQuery(queryParams, {
        ...queryOptions,
        skip: skipQuery || isV2Enabled,
    });
    const queryV2 = productApi.useGetHomeProductV2Query(queryParams, {
        ...queryOptions,
        skip: skipQuery || !isV2Enabled,
    });
    const contaDigitalQuery = isV2Enabled ? queryV2 : queryV1;
    useEffect(() => {
        if (contaDigitalQuery.isLoading) {
            dispatch(setProductLoading({
                productAlias: ProductAlias.CONTA_DIGITAL,
                isLoading: true,
            }));
            return;
        }
        if (contaDigitalQuery.error) {
            dispatch(setProductError({
                productAlias: ProductAlias.CONTA_DIGITAL,
                error: 'Falha ao carregar produto Conta Digital',
            }));
            return;
        }
        if (contaDigitalQuery.data &&
            typeof contaDigitalQuery.data === 'object' &&
            contaDigitalQuery.data !== null &&
            'result' in contaDigitalQuery.data) {
            const result = contaDigitalQuery.data.result;
            const normalized = normalizeProductResponse(result);
            dispatch(setProductData({
                productAlias: ProductAlias.CONTA_DIGITAL,
                data: normalized,
            }));
        }
    }, [contaDigitalQuery.isLoading, contaDigitalQuery.error, contaDigitalQuery.data, dispatch]);
    return {
        data: contaDigitalProduct.data?.metadata || null,
        isLoading: contaDigitalProduct.isLoading,
        error: contaDigitalProduct.error,
        refetch: () => {
            contaDigitalQuery.refetch();
        },
    };
};
